import { useState, useMemo, useCallback } from "react";

export interface FilterConfig {
  column: string;
  value: string;
  type: "text" | "date";
}

export function useClientFilter<T extends Record<string, any>>(data: T[]) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [filterTypes, setFilterTypes] = useState<Record<string, "text" | "date">>({});

  const setFilter = useCallback((column: string, value: string, type: "text" | "date") => {
    setFilters((prev) => ({ ...prev, [column]: value }));
    setFilterTypes((prev) => ({ ...prev, [column]: type }));
  }, []);

  const clearFilter = useCallback((column: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : "";
    }, obj);
  };

  const filteredData = useMemo(() => {
    let result = [...data];

    Object.entries(filters).forEach(([column, value]) => {
      if (!value || value === "") return;
      const lowerValue = String(value).toLowerCase();

      result = result.filter((item) => {
        const fieldValue = getNestedValue(item, column);
        if (fieldValue === null || fieldValue === undefined) return false;
        return String(fieldValue).toLowerCase().includes(lowerValue);
      });
    });

    return result;
  }, [data, filters]);

  return {
    filteredData,
    filters,
    setFilter,
    clearFilter,
    clearAllFilters,
    hasActiveFilters,
  };
}
